import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import type {
  DecideRolePermissionOffersBody,
  RolePermissionOffersListView,
  RolePermissionOffersView,
} from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  EmptyState,
  LoadingBlock,
  Notice,
  Row,
  Section,
  Table,
  TableBody,
  TableHeader,
} from '@wodi/ui';

import { ApiError } from '../api/client.ts';
import { decideRoleOffers, roleOffersQuery } from '../api/roles.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { screen, TableScroll } from '../layout/screen.tsx';
import { SettingsPage } from '../routes/settings-nav.tsx';
import { toasts } from '../toasts.ts';
import styles from './role-updates.module.css';

// Settings > Role updates (ADR 0031, docs/plans/role-permission-upgrades.md §5): what a
// release added to the standard roles, offered to this organisation's roles. Nothing is
// granted until an administrator accepts it; each press saves at once and is audited.

function errorText(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

function decidedToast(roleName: string, body: DecideRolePermissionOffersBody): string {
  const accepted = body.accept?.length ?? 0;
  if (accepted > 1) return `${String(accepted)} permissions added to ${roleName}`;
  if (accepted === 1) return `Permission added to ${roleName}`;
  return `Permission declined for ${roleName}`;
}

function RoleOffers({ role }: { role: RolePermissionOffersView }) {
  const queryClient = useQueryClient();
  const online = useOnline();
  const decide = useMutation({
    mutationFn: (body: DecideRolePermissionOffersBody) => decideRoleOffers(role.roleId, body),
    onSuccess: (next: RolePermissionOffersListView, body) => {
      queryClient.setQueryData(roleOffersQuery.queryKey, next);
      toasts.show({ title: decidedToast(role.roleName, body) });
    },
    // Someone else may have decided first: show what is still on offer.
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: roleOffersQuery.queryKey });
    },
  });
  const count = role.permissions.length;
  return (
    <Section
      title={role.roleName}
      description={`Made from the ${role.templateName} template.`}
      actions={
        <Button
          isDisabled={!online || decide.isPending}
          onPress={() => {
            decide.mutate({ accept: role.permissions.map((p) => p.permission) });
          }}
        >
          Accept all
        </Button>
      }
    >
      <p>
        {count === 1
          ? '1 new permission. Accept it to give it to everyone with this role.'
          : `${String(count)} new permissions. Accept those this role should have; declined ones are not offered again.`}
      </p>
      <TableScroll>
        <Table flat aria-label={`New permissions for ${role.roleName}`}>
          <TableHeader>
            <Column isRowHeader>Lets the role</Column>
            <Column>Permission</Column>
            <Column>Decision</Column>
          </TableHeader>
          <TableBody items={role.permissions.map((p) => ({ ...p, id: p.permission }))}>
            {(p) => (
              <Row id={p.id}>
                <Cell>{p.description}</Cell>
                <Cell>
                  <span className={styles.code}>{p.permission}</span>
                </Cell>
                <Cell>
                  <span className={styles.decision}>
                    <Button
                      dense
                      isDisabled={!online || decide.isPending}
                      onPress={() => {
                        decide.mutate({ accept: [p.permission] });
                      }}
                    >
                      Accept
                    </Button>
                    <Button
                      dense
                      variant="quiet"
                      isDisabled={!online || decide.isPending}
                      onPress={() => {
                        decide.mutate({ decline: [p.permission] });
                      }}
                    >
                      Decline
                    </Button>
                  </span>
                </Cell>
              </Row>
            )}
          </TableBody>
        </Table>
      </TableScroll>
      {decide.error === null ? null : (
        <Notice tone="error">
          {errorText(decide.error, 'The decision was not saved. Try again.')}
        </Notice>
      )}
    </Section>
  );
}

export function RoleUpdatesPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const allowed = session !== null && can(session, 'admin.roles.manage');
  const offers = useQuery({ ...roleOffersQuery, enabled: allowed });
  const online = useOnline();
  if (!allowed) {
    return (
      <SettingsPage title="Role updates">
        <Notice tone="info">
          You don&apos;t have permission to change roles. Ask an administrator if you need it.
        </Notice>
      </SettingsPage>
    );
  }
  return (
    <SettingsPage title="Role updates">
      <div className={screen.page}>
        {online ? null : (
          <Notice tone="info">
            No connection to Wodi. The list shows what was last loaded; decisions need the
            connection.
          </Notice>
        )}
        {offers.isPending ? <LoadingBlock label="Loading role updates" rows={3} /> : null}
        {offers.isError ? (
          <Notice
            tone="error"
            action={
              <Button dense onPress={() => void offers.refetch()}>
                Try again
              </Button>
            }
          >
            {errorText(offers.error, "Role updates couldn't be loaded. Try again.")}
          </Notice>
        ) : null}
        {offers.isSuccess && offers.data.roles.length === 0 ? (
          <EmptyState>Every role has the permissions its template offers.</EmptyState>
        ) : null}
        {offers.isSuccess
          ? offers.data.roles.map((role) => <RoleOffers key={role.roleId} role={role} />)
          : null}
      </div>
    </SettingsPage>
  );
}
